import { useEffect, useRef, useState } from "react";
import { Loader2, Paperclip, Send } from "lucide-react";
import {
    getLauraChatMessages,
    streamLauraChat,
    uploadLauraAuditDocument,
} from "@/app/lib/auditflowApi";
import { useTranslation } from "@/app/i18n/LocaleContext";

interface Message {
    role: "user" | "assistant";
    content: string;
}

interface Props {
    auditId: string;
    currentStage?: string;
    onDeliverableUpdated?: () => void;
    /** When true, omit outer title bar (parent provides chrome). */
    embedded?: boolean;
}

export function LauraChatPanel({
    auditId,
    currentStage,
    onDeliverableUpdated,
    embedded = false,
}: Props) {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        getLauraChatMessages(auditId)
            .then((rows) => {
                const parsed: Message[] = [];
                for (const row of rows) {
                    const text = Array.isArray(row.content)
                        ? row.content
                              .filter((c) => c.type === "text")
                              .map((c) => c.text ?? "")
                              .join("")
                        : "";
                    if (row.role === "user" || row.role === "assistant") {
                        parsed.push({
                            role: row.role,
                            content: text,
                        });
                    }
                }
                setMessages(parsed);
            })
            .finally(() => setInitialized(true));
    }, [auditId]);

    async function send() {
        const text = input.trim();
        if (!text || loading) return;
        setInput("");
        const nextMessages: Message[] = [
            ...messages,
            { role: "user", content: text },
            { role: "assistant", content: "" },
        ];
        setMessages(nextMessages);
        setLoading(true);
        abortRef.current?.abort();
        abortRef.current = new AbortController();

        try {
            const res = await streamLauraChat({
                auditId,
                messages: [...messages, { role: "user", content: text }],
                signal: abortRef.current.signal,
            });
            if (!res.ok || !res.body) throw new Error("Stream failed");

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let assistantText = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop() ?? "";
                for (const part of parts) {
                    const line = part.trim();
                    if (!line.startsWith("data: ")) continue;
                    const payload = line.slice(6);
                    if (payload === "[DONE]") continue;
                    try {
                        const data = JSON.parse(payload) as {
                            type: string;
                            text?: string;
                        };
                        if (data.type === "content_delta" && data.text) {
                            assistantText += data.text;
                            setMessages((prev) => {
                                const copy = [...prev];
                                const last = copy[copy.length - 1];
                                if (last?.role === "assistant") {
                                    copy[copy.length - 1] = {
                                        ...last,
                                        content: assistantText,
                                    };
                                }
                                return copy;
                            });
                        }
                        if (data.type === "deliverable_updated") {
                            onDeliverableUpdated?.();
                        }
                    } catch {
                        /* ignore parse errors */
                    }
                }
            }
        } catch (err) {
            if ((err as Error).name !== "AbortError") {
                setMessages((prev) => {
                    const copy = [...prev];
                    const last = copy[copy.length - 1];
                    if (last?.role === "assistant" && !last.content) {
                        copy[copy.length - 1] = {
                            role: "assistant",
                            content: "Sorry, something went wrong.",
                        };
                    }
                    return copy;
                });
            }
        } finally {
            setLoading(false);
        }
    }

    async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            await uploadLauraAuditDocument(auditId, file, "reference");
            onDeliverableUpdated?.();
        } catch {
            /* ignore */
        }
        e.target.value = "";
    }

    if (!initialized) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col bg-white">
            {!embedded && (
                <div className="shrink-0 border-b px-4 py-3 text-sm font-medium">
                    LAURA Chat
                </div>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 && (
                    <p className="text-sm text-gray-500">
                        {currentStage === "p0"
                            ? t("laura", "p0ChatEmpty")
                            : "Start a conversation to work on the current stage deliverable."}
                    </p>
                )}
                {messages.map((m, i) => (
                    <div
                        key={i}
                        className={`text-sm whitespace-pre-wrap ${
                            m.role === "user" ? "text-gray-900" : "text-gray-700"
                        }`}
                    >
                        <span className="font-medium capitalize">{m.role}: </span>
                        {m.content || (loading && i === messages.length - 1 ? "…" : "")}
                    </div>
                ))}
            </div>
            <div className="border-t p-3">
                <div className="flex items-end gap-2">
                    <label className="cursor-pointer rounded-lg p-2 text-gray-500 hover:bg-gray-50">
                        <Paperclip className="h-4 w-4" />
                        <input type="file" className="hidden" onChange={handleFile} />
                    </label>
                    <textarea
                        className="min-h-[40px] flex-1 resize-none rounded-lg border px-3 py-2 text-sm outline-none"
                        rows={2}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                        placeholder="Message LAURA…"
                    />
                    <button
                        type="button"
                        onClick={send}
                        disabled={loading || !input.trim()}
                        className="rounded-lg bg-gray-900 p-2 text-white disabled:opacity-50"
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="h-4 w-4" />
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
